import { Button, Input, TOUCH } from "@nymbl/nos2-rules";
import { Check } from "lucide-react";
import { MeetingTaskPicker } from "@/components/calendar/MeetingTaskPicker";
import {
  hoursProblem,
  hoursText,
  isChanged,
  meetingName,
  type PanelRow,
  rowHours,
} from "@/lib/calendar/panel";
import { taskLabel } from "@/lib/time/labels";
import { cn } from "@/lib/utils";
import type { AssignableTask } from "@/schemas/time";

interface MeetingRowProps {
  row: PanelRow;
  index: number;
  /** This row's Accept, Dismiss or Undo is in flight (or "Accept N matched" is running). */
  busy: boolean;
  /** The server's text from this row's last failed Accept, kept until the next try. */
  error: string | null;
  /** An earlier Accept may have saved under this row's entry id: the task can no longer change. */
  locked: boolean;
  onPick: (task: AssignableTask) => void;
  /** The hours box changed (typed text; Accept checks it). */
  onHours: (hours: string) => void;
  onAccept: () => void;
  onDismiss: () => void;
  onUndo: () => void;
  /** A logged meeting: remove its entry and put the meeting back to pick the task again. */
  onUndoAccept: () => void;
}

/**
 * One meeting: when, what, who, the hours to log (the meeting's length until changed), the task
 * it goes to and why, Accept and Dismiss. One line on wide screens; a stacked card on phones.
 */
export function MeetingRow({
  row,
  index,
  busy,
  error,
  locked,
  onPick,
  onHours,
  onAccept,
  onDismiss,
  onUndo,
  onUndoAccept,
}: MeetingRowProps) {
  const { meeting, chosen, state } = row;
  const name = meetingName(meeting);
  const pending = state.kind === "pending";
  const reason =
    pending && isChanged(row) ? "Changed · will remember for next time" : meeting.reason;
  const problem = pending ? hoursProblem(row) : null;
  const hoursId = `meeting-hours-${index}`;
  const errorId = `meeting-error-${index}`;
  const hours = pending ? (
    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <Input
        inputMode="decimal"
        autoComplete="off"
        aria-label={`Hours for ${name}`}
        aria-invalid={problem ? true : undefined}
        aria-describedby={problem ? hoursId : undefined}
        disabled={busy || locked}
        value={row.hours}
        onChange={(e) => onHours(e.target.value)}
        className={cn(TOUCH, "w-16 text-right text-foreground tabular-nums")}
      />
      hrs
    </span>
  ) : (
    <span className="text-sm tabular-nums">{hoursText(rowHours(row) ?? meeting.hours)}</span>
  );

  return (
    <li
      className={cn(
        "grid gap-x-4 gap-y-2 py-3 max-sm:rounded-lg max-sm:border max-sm:px-3",
        "sm:grid-cols-[6.5rem_minmax(0,1fr)_6rem_minmax(12rem,20rem)_auto] sm:items-center",
        state.kind === "dismissed" && "opacity-60",
      )}
    >
      <div className="text-sm font-semibold tabular-nums">
        {meeting.dayLabel}{" "}
        <span className="font-normal text-muted-foreground">{meeting.timeLabel}</span>
      </div>
      <div className="min-w-0">
        <p className="truncate font-semibold">{meeting.title}</p>
        <p className="truncate text-xs text-muted-foreground">{meeting.who}</p>
      </div>
      <div>{hours}</div>
      <div className="min-w-0 space-y-1">
        {pending ? (
          <MeetingTaskPicker
            name={name}
            index={index}
            task={chosen}
            onPick={onPick}
            disabled={busy || locked}
          />
        ) : (
          <p className="truncate text-sm">
            {chosen ? taskLabel(chosen.taskName, chosen.projectName) : ""}
          </p>
        )}
        {pending && reason && <p className="truncate text-xs text-muted-foreground">{reason}</p>}
      </div>
      <div className="flex items-center gap-2 sm:justify-end">
        {pending && (
          <>
            <Button
              className={TOUCH}
              disabled={busy || !chosen || problem !== null}
              aria-label={`Accept ${name}`}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              onClick={onAccept}
            >
              Accept
            </Button>
            <Button
              variant="ghost"
              className={TOUCH}
              disabled={busy}
              aria-label={`Dismiss ${name}`}
              onClick={onDismiss}
            >
              Dismiss
            </Button>
          </>
        )}
        {state.kind === "logged" && (
          <>
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-success">
              <Check className="size-4" aria-hidden="true" />
              Logged
            </span>
            <Button
              variant="outline"
              className={TOUCH}
              disabled={busy}
              aria-label={`Undo log ${name}`}
              onClick={onUndoAccept}
            >
              Undo
            </Button>
          </>
        )}
        {state.kind === "dismissed" && (
          <>
            <span className="text-sm text-muted-foreground">Dismissed</span>
            <Button
              variant="outline"
              className={TOUCH}
              disabled={busy}
              aria-label={`Undo dismiss ${name}`}
              onClick={onUndo}
            >
              Undo
            </Button>
          </>
        )}
      </div>
      {problem && (
        <p className="text-sm text-danger sm:col-span-5" id={hoursId}>
          {problem}
        </p>
      )}
      {error && (
        <p className="text-sm text-danger sm:col-span-5" id={errorId}>
          {error}
        </p>
      )}
    </li>
  );
}
